// ST-1258 -Claude- Nearrme app shell — top bar + routes + connection banner.
import { useEffect, useState } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConnectionBanner } from '@nearrme/ui-web';
import { logout } from '@nearrme/api-client';
import { LandingPage } from './pages/LandingPage';
import { SearchPage } from './pages/SearchPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { BusinessDetailPage } from './pages/BusinessDetailPage';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { BusinessSignupPage } from './pages/BusinessSignupPage';
import { AboutPage } from './pages/AboutPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { ThemeToggle } from './theme/ThemeToggle';
import { LanguagePicker } from './components/LanguagePicker';
import { useNetworkStatus } from './hooks/useNetworkStatus';
import { getApiClient } from './api/client';
import { AUTH_CHANGED_EVENT, clearConsumerToken, getConsumerToken } from './auth/session';
import { RouteAnalytics } from './analytics/RouteAnalytics';
import { BAG_MARK_SVG, BRAND } from '@nearrme/brand';
// ST-32 -Claude- Business portal routes.
import { AdminLayout } from './admin/AdminLayout';
import { AdminOnlyRoute } from './admin/AdminOnlyRoute';
import { ProtectedAdminRoute } from './admin/ProtectedAdminRoute';
import { AdminLoginPage } from './admin/pages/AdminLoginPage';
import { AdminDashboardPage } from './admin/pages/AdminDashboardPage';
import { AdminCompaniesPage } from './admin/pages/AdminCompaniesPage';
import { AdminCompanyDetailPage } from './admin/pages/AdminCompanyDetailPage';
import { AdminBrandsPage } from './admin/pages/AdminBrandsPage';
import { AdminStoreDetailPage, AdminStoresPage } from './admin/pages/AdminStoresPage';
import { AdminProductsPage } from './admin/pages/AdminProductsPage';
import { AdminUsersPage } from './admin/pages/AdminUsersPage';
import { AdminNearrmeTeamPage } from './admin/pages/AdminNearrmeTeamPage';
import { AdminConsumersPage } from './admin/pages/AdminConsumersPage';
import { AdminOrdersPage } from './admin/pages/AdminOrdersPage';
import { AdminContactMessagesPage } from './admin/pages/AdminContactMessagesPage';
import { AdminAgentsPage } from './admin/pages/AdminAgentsPage';
import { AdminLogsPage } from './admin/pages/AdminLogsPage';
import { AdminLoginOptionsPage } from './admin/pages/AdminLoginOptionsPage';
import { AdminRolesPage } from './admin/pages/AdminRolesPage';
import { AdminPermissionsPage } from './admin/pages/AdminPermissionsPage';
import { AdminHelpPage } from './admin/pages/AdminHelpPage';
import {
  AdminBrandDetailPage,
  AdminConsumerDetailPage,
  AdminContactMessageDetailPage,
  AdminOrderDetailPage,
  AdminProductDetailPage,
} from './admin/pages/AdminRecordDetails';

export function App() {
  const location = useLocation();

  // ST-32 -Claude- Business portal uses a separate layout (no public chrome).
  if (location.pathname.startsWith('/admin') || location.pathname.startsWith('/business/login')) {
    return (
      <Routes>
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/business/login" element={<AdminLoginPage />} />
        <Route
          path="/admin"
          element={
            <ProtectedAdminRoute>
              <AdminLayout />
            </ProtectedAdminRoute>
          }
        >
          <Route index element={<AdminDashboardPage />} />
          <Route path="companies" element={<AdminCompaniesPage />} />
          <Route path="companies/:companyCode" element={<AdminCompanyDetailPage />} />
          <Route path="brands" element={<AdminBrandsPage />} />
          <Route path="brands/:brandId" element={<AdminBrandDetailPage />} />
          <Route path="stores" element={<AdminStoresPage />} />
          <Route path="stores/:storeId" element={<AdminStoreDetailPage />} />
          <Route path="products" element={<AdminProductsPage />} />
          <Route path="products/:productId" element={<AdminProductDetailPage />} />
          <Route path="orders" element={<AdminOrdersPage />} />
          <Route path="orders/:orderId" element={<AdminOrderDetailPage />} />
          <Route path="consumers" element={<AdminConsumersPage />} />
          <Route path="consumers/:consumerId" element={<AdminConsumerDetailPage />} />
          <Route path="nearrme-team" element={<AdminNearrmeTeamPage />} />
          <Route path="company-team" element={<AdminUsersPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="login-options" element={<AdminLoginOptionsPage />} />
          <Route path="roles" element={<AdminRolesPage />} />
          <Route path="permissions" element={<AdminPermissionsPage />} />
          <Route path="setup/login-options" element={<AdminLoginOptionsPage />} />
          <Route path="setup/roles" element={<AdminRolesPage />} />
          <Route path="setup/permissions" element={<AdminPermissionsPage />} />
          <Route path="setup/activity" element={<AdminLogsPage />} />
          <Route path="contact-messages" element={<AdminContactMessagesPage />} />
          <Route path="contact-messages/:messageId" element={<AdminContactMessageDetailPage />} />
          <Route path="help" element={<AdminHelpPage />} />
          <Route path="help/:topicSlug" element={<AdminHelpPage />} />
          <Route path="agents" element={<AdminOnlyRoute><AdminAgentsPage /></AdminOnlyRoute>} />
          <Route path="logs" element={<AdminLogsPage />} />
        </Route>
      </Routes>
    );
  }

  return <PublicShell />;
}

function PublicShell() {
  const { t } = useTranslation();
  const network = useNetworkStatus();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(() => Boolean(getConsumerToken()));

  useEffect(() => {
    const syncAuthState = () => setIsLoggedIn(Boolean(getConsumerToken()));
    window.addEventListener(AUTH_CHANGED_EVENT, syncAuthState);
    window.addEventListener('storage', syncAuthState);
    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, syncAuthState);
      window.removeEventListener('storage', syncAuthState);
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await logout(getApiClient());
    } catch (error) {
      console.warn('[logout failed]', error);
    } finally {
      clearConsumerToken();
      setMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <RouteAnalytics />
      <ConnectionBanner state={network} />
      <header className="sticky top-0 z-40 bg-white/95 dark:bg-slate-950/95 backdrop-blur border-b border-slate-200 dark:border-slate-800">
        <div className="border-b border-slate-100 dark:border-slate-900">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 h-10 flex items-center justify-end">
            <div className="flex items-center gap-2">
              <LanguagePicker />
              <ThemeToggle />
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 min-h-16 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 min-w-0">
            <span
              className="w-9 h-9 shrink-0"
              dangerouslySetInnerHTML={{ __html: BAG_MARK_SVG }}
            />
            <span className="block text-xl sm:text-2xl font-light tracking-[0.08em] text-slate-950 dark:text-slate-50 truncate">
              Nea<span className="font-normal text-accent-red">r</span><span className="font-normal text-brand-500 dark:text-brand-400">r</span>me
            </span>
          </Link>
          <nav className="ml-auto hidden md:flex items-center gap-2">
            <Link
              to="/about"
              className="text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-brand-500 px-3"
            >
              About
            </Link>
            <Link
              to="/business/signup"
              className="text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-brand-500 px-3"
            >
              For business
            </Link>
            <Link
              to="/business/login"
              className="text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-brand-500 px-3"
            >
              Business login
            </Link>
            {isLoggedIn ? (
              <button
                type="button"
                onClick={handleSignOut}
                className="text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-brand-500 px-3"
              >
                Sign out
              </button>
            ) : (
              <>
                {location.pathname !== '/signup' && (
                  <Link
                    to="/signup"
                    className="text-sm font-medium text-brand-700 dark:text-brand-300 hover:text-brand-500 px-3"
                  >
                    {t('nav.signup')}
                  </Link>
                )}
                {location.pathname !== '/login' && (
                  <Link
                    to="/login"
                    className="text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-brand-500 px-3"
                  >
                    {t('nav.login')}
                  </Link>
                )}
              </>
            )}
          </nav>
          <div className="ml-auto md:hidden flex items-center gap-1">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200"
              aria-expanded={menuOpen}
              aria-label="Open menu"
            >
              Menu
            </button>
          </div>
        </div>
        {menuOpen && (
          <nav className="md:hidden border-t border-slate-200 dark:border-slate-800 px-3 py-3 grid gap-2 bg-white dark:bg-slate-950">
            <Link
              to="/"
              onClick={() => setMenuOpen(false)}
              className="px-3 py-2 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              Home
            </Link>
            {!isLoggedIn && (
              <>
                <Link
                  to="/signup"
                  onClick={() => setMenuOpen(false)}
                  className="px-3 py-2 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  {t('nav.signup')}
                </Link>
                <Link
                  to="/login"
                  onClick={() => setMenuOpen(false)}
                  className="px-3 py-2 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  {t('nav.login')}
                </Link>
              </>
            )}
            <Link
              to="/business/signup"
              onClick={() => setMenuOpen(false)}
              className="px-3 py-2 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              For business
            </Link>
            <Link
              to="/business/login"
              onClick={() => setMenuOpen(false)}
              className="px-3 py-2 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              Business login
            </Link>
            <Link
              to="/about"
              onClick={() => setMenuOpen(false)}
              className="px-3 py-2 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              About
            </Link>
            {isLoggedIn && (
              <button
                type="button"
                onClick={handleSignOut}
                className="text-left px-3 py-2 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Sign out
              </button>
            )}
          </nav>
        )}
      </header>

      <main className="flex-1">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/p/:productId" element={<ProductDetailPage />} />
          <Route path="/b/:businessId" element={<BusinessDetailPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/business/signup" element={<BusinessSignupPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-800 mt-12 bg-slate-50 dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 py-10 grid gap-8 md:grid-cols-[1.4fr_1fr_1fr_1fr] text-sm">
          <div>
            <Link to="/" className="inline-flex items-center gap-2">
              <span className="w-9 h-9" dangerouslySetInnerHTML={{ __html: BAG_MARK_SVG }} />
              <span className="text-xl font-light tracking-[0.08em] text-slate-950 dark:text-slate-50">
                Nea<span className="font-normal text-accent-red">r</span><span className="font-normal text-brand-500 dark:text-brand-400">r</span>me
              </span>
            </Link>
            <p className="mt-4 max-w-sm text-slate-600 dark:text-slate-300">{BRAND.tagline}</p>
            <p className="mt-2 max-w-sm text-slate-500 dark:text-slate-400">{BRAND.trustLine}</p>
          </div>

          <div>
            <h2 className="font-bold text-slate-950 dark:text-slate-50">Consumers</h2>
            <div className="mt-3 grid gap-2 text-slate-600 dark:text-slate-300">
              <Link to="/" className="hover:text-brand-600">Search products</Link>
              <Link to="/signup" className="hover:text-brand-600">Create account</Link>
              <Link to="/login" className="hover:text-brand-600">Login</Link>
            </div>
          </div>

          <div>
            <h2 className="font-bold text-slate-950 dark:text-slate-50">Businesses</h2>
            <div className="mt-3 grid gap-2 text-slate-600 dark:text-slate-300">
              <Link to="/business/signup" className="hover:text-brand-600">List your store</Link>
              <Link to="/business/login" className="hover:text-brand-600">Business login</Link>
              <span>Verified stores</span>
            </div>
          </div>

          <div>
            <h2 className="font-bold text-slate-950 dark:text-slate-50">Company</h2>
            <div className="mt-3 grid gap-2 text-slate-600 dark:text-slate-300">
              <Link to="/about" className="hover:text-brand-600">About</Link>
              <span>nearrme.com</span>
              <span>{BRAND.trademarkNotice}</span>
            </div>
          </div>
        </div>
        <div className="border-t border-slate-200 dark:border-slate-800 py-4">
          <div className="max-w-7xl mx-auto px-4 text-xs text-slate-500 dark:text-slate-400 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
            <span>Nearrme is a registered trademark.</span>
            <span>Powered by Uniworld Technologies Pvt. Ltd.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
